import React, { useState, useEffect, useCallback } from 'react';
import { Bell } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext'; // Assuming you have an AuthContext
import { Badge } from '../ui/badge';
import NotificationList from './NotificationList';
import { toast } from 'sonner'; // Import toast
import type { Database } from '../../types/database'; // Import Database type
import { useNavigate } from 'react-router-dom'; // For navigation

const NotificationBell: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate(); // For navigation
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [showList, setShowList] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) {
        console.error('Error fetching unread count:', error);
        setUnreadCount(0);
      } else {
        setUnreadCount(count ?? 0);
      }
    } catch (err) {
        console.error('Error in fetchUnreadCount:', err);
        setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }

    // Initial fetch
    fetchUnreadCount();

    // Set up Supabase Realtime subscription
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen for INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`, // Only listen for changes for the current user
        },
        (payload) => {
          console.log('Realtime notification change received:', payload);
          // Refetch count on any change (insert, update read status, delete)
          fetchUnreadCount();

          if (payload.eventType === 'INSERT') {
            const newNotification = payload.new as Database['public']['Tables']['notifications']['Row'];
            // Use newNotification.message directly as it's now formatted by the trigger
            const toastMessage = newNotification.message || 'You have a new notification.';
            const notificationLink = newNotification.link;

            toast.info(toastMessage, {
              action: notificationLink ? {
                label: 'View',
                onClick: () => {
                  navigate(notificationLink); // Navigate to the link
                  if (showList) toggleList(); // Close list if open
                },
              } : undefined,
              // Add other toast options if needed, e.g., duration
            });
          }
        }
      )
      .subscribe((status, err) => {
         if (status === 'SUBSCRIBED') {
             console.log('Subscribed to notifications channel');
         }
         if (status === 'CHANNEL_ERROR') {
             console.error('Notifications channel error:', err);
         }
         if (status === 'TIMED_OUT') {
            console.warn('Notifications channel subscription timed out.');
         }
      });

    // Cleanup function to unsubscribe
    return () => {
      supabase.removeChannel(channel);
      console.log('Unsubscribed from notifications channel');
    };
  }, [user, fetchUnreadCount, showList, navigate]); // Added showList and navigate to deps

  const toggleList = () => {
    setShowList((prev) => !prev);
  };

  const handleCloseList = () => {
    setShowList(false);
    // Optionally refetch count when list is closed, in case items were marked read
    fetchUnreadCount();
  };

  return (
    <div className="relative">
      <button
        onClick={toggleList}
        className="relative p-2 rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        aria-label="Notifications"
        disabled={isLoading}
      >
        <Bell className="h-6 w-6" />
        {isLoading ? (
          <Badge variant="notificationLoading">?</Badge>
        ) : unreadCount > 0 && (
          <Badge variant="notification">
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </button>

      {/* Dropdown/Modal for Notification List */}
      {showList && (
        <div className="absolute right-0 mt-2 w-80 md:w-96 origin-top-right bg-white border border-gray-200 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
           {/* We will pass necessary props here */}
          <NotificationList onClose={handleCloseList} />
        </div>
      )}
    </div>
  );
};

export default NotificationBell; 